import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentType,
  type RefObject,
} from 'react';
import { useWidget } from '../context/WidgetContext.js';
import {
  loadEmojiPickerData,
  type EmojiData,
  type EmojiPickerComponent,
} from '../emoji/load-emoji-data.js';
import type { UiLocale } from '../i18n/ui-strings.js';
import { IconSmile } from './icons.js';

const PICKER_HEIGHT = 240;

/** Avoid static `import … from 'emoji-picker-react'` (pulls the whole lib into main). */
type EmojiClickData = { emoji: string };

type UseChatEmojiPickerOptions = {
  disabled?: boolean;
  onSelect: (emoji: string) => void;
  inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
};

type PickerModule = {
  default: EmojiPickerComponent;
  EmojiStyle: { NATIVE: unknown };
  Theme: { LIGHT: unknown };
};

/**
 * Emoji UI loads `emoji-picker-react` (+ locale data) only when the panel opens.
 * Keeps the picker out of the initial chat shell chunk.
 */
export function useChatEmojiPicker({
  disabled = false,
  onSelect,
  inputRef,
}: UseChatEmojiPickerOptions) {
  const { locale, t } = useWidget();
  const rootRef = useRef<HTMLDivElement>(null);
  const pickerId = useId();
  const [open, setOpen] = useState(false);
  const [emojiData, setEmojiData] = useState<EmojiData | undefined>(undefined);
  const [pickerMod, setPickerMod] = useState<PickerModule | null>(null);
  const pickerLocale = locale as UiLocale;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    void Promise.all([
      import('emoji-picker-react'),
      loadEmojiPickerData(pickerLocale),
    ])
      .then(([mod, data]) => {
        if (cancelled) return;
        setPickerMod({
          default: mod.default,
          EmojiStyle: mod.EmojiStyle,
          Theme: mod.Theme,
        });
        setEmojiData(data);
      })
      .catch(() => {
        // Keep the loading panel; an unhandled rejection can tear down the chat tree.
      });

    return () => {
      cancelled = true;
    };
  }, [pickerLocale, open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const path = event.composedPath();
      if (rootRef.current && path.includes(rootRef.current)) return;
      setOpen(false);
    };

    // Attach after the opening tap/click finishes. Playwright mobile (and iOS)
    // can synthesize a leftover pointerdown that would close the panel immediately.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
    }, 0);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const handleToggle = useCallback(() => {
    if (disabled) return;
    const nextOpen = !open;
    setOpen(nextOpen);

    const input = inputRef.current;
    if (!input) return;

    const coarsePointer =
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: coarse)').matches;
    const inputAlreadyFocused = input.ownerDocument.activeElement === input;

    // On touch devices, opening the in-widget picker must not also summon the
    // system keyboard. If the keyboard is already open, preserve that state.
    if (!coarsePointer || inputAlreadyFocused) {
      input.focus();
    }
  }, [disabled, inputRef, open]);

  const handleEmojiClick = useCallback(
    (emoji: EmojiClickData) => {
      onSelect(emoji.emoji);
      setOpen(false);
      inputRef.current?.focus();
    },
    [inputRef, onSelect],
  );

  const Picker = pickerMod?.default as ComponentType<{
    open: boolean;
    onEmojiClick: (emoji: EmojiClickData) => void;
    emojiData?: EmojiData;
    theme: unknown;
    emojiStyle: unknown;
    width: string;
    height: number;
    lazyLoadEmojis: boolean;
    searchDisabled: boolean;
    autoFocusSearch: boolean;
    previewConfig: { showPreview: boolean };
  }> | undefined;

  const panel =
    open ? (
      <div
        id={pickerId}
        className="teo-emoji-picker__panel"
        role="dialog"
        aria-modal="false"
        aria-label={t.openEmojiPicker}
      >
        {Picker && pickerMod ? (
          <Picker
            open
            onEmojiClick={handleEmojiClick}
            emojiData={emojiData}
            theme={pickerMod.Theme.LIGHT}
            emojiStyle={pickerMod.EmojiStyle.NATIVE}
            width="100%"
            height={PICKER_HEIGHT}
            lazyLoadEmojis
            searchDisabled
            autoFocusSearch={false}
            previewConfig={{ showPreview: false }}
          />
        ) : (
          <div className="teo-emoji-picker__loading" aria-busy="true" />
        )}
      </div>
    ) : null;

  const buttonClassName = `teo-icon-btn teo-icon-btn--input teo-emoji-btn${
    open ? ' teo-emoji-btn--active' : ''
  }`;

  const button = (
    <button
      type="button"
      className={buttonClassName}
      aria-label={t.openEmojiPicker}
      aria-expanded={open}
      aria-controls={open ? pickerId : undefined}
      onClick={handleToggle}
      disabled={disabled}
    >
      <IconSmile />
    </button>
  );

  return { rootRef, panel, button, open };
}
